import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  notificationPreferenceRepository,
  type NotificationPreferenceView,
} from "../repositories/notificationPreferenceRepository";

export const notificationPreferencesQueryKey = () =>
  ["notification-preferences"] as const;

const preferenceMutationKey = ["notification-preferences"] as const;

type NotificationPreferencesQueryOptions = {
  enabled?: boolean;
};

type ChannelKey = Exclude<keyof NotificationPreferenceView, "typePreferences">;
type ChannelValues = Partial<Pick<NotificationPreferenceView, ChannelKey>>;
type TypePreference = NotificationPreferenceView["typePreferences"][number];

type ChannelsMutationContext = {
  previous: ChannelValues;
  optimistic: ChannelValues;
};

type TypeMutationContext = {
  previous?: TypePreference;
};

const channelKeys = (values: ChannelValues) =>
  Object.keys(values) as ChannelKey[];

const pickChangedChannels = (
  current: NotificationPreferenceView,
  settings: Partial<NotificationPreferenceView>,
): ChannelValues => {
  const changed: ChannelValues = {};
  for (const [key, value] of Object.entries(settings)) {
    if (key === "typePreferences" || typeof value !== "boolean") continue;
    if (current[key as ChannelKey] !== value) {
      changed[key as ChannelKey] = value;
    }
  }
  return changed;
};

const withTypePreference = (
  preferences: NotificationPreferenceView,
  type: string,
  next: TypePreference | undefined,
): NotificationPreferenceView => {
  const others = preferences.typePreferences.filter(
    (preference) => preference.type !== type,
  );
  const exists = others.length !== preferences.typePreferences.length;
  if (!next) return { ...preferences, typePreferences: others };
  return {
    ...preferences,
    typePreferences: exists
      ? preferences.typePreferences.map((preference) =>
          preference.type === type ? next : preference,
        )
      : [...preferences.typePreferences, next],
  };
};

// The finishing mutation is still pending while its own callbacks run.
const hasOtherPendingPreferenceUpdates = (queryClient: QueryClient) =>
  queryClient.isMutating({ mutationKey: preferenceMutationKey }) > 1;

export const useNotificationPreferencesQuery = (
  options?: NotificationPreferencesQueryOptions,
) =>
  useQuery({
    queryKey: notificationPreferencesQueryKey(),
    queryFn: () => notificationPreferenceRepository.get(),
    enabled: options?.enabled ?? true,
  });

export const useUpdateNotificationChannelsMutation = () => {
  const queryClient = useQueryClient();
  const queryKey = notificationPreferencesQueryKey();

  return useMutation<
    NotificationPreferenceView,
    Error,
    Partial<NotificationPreferenceView>,
    ChannelsMutationContext
  >({
    mutationKey: [...preferenceMutationKey, "channels"],
    mutationFn: (settings) =>
      notificationPreferenceRepository.updateChannels(settings),
    onMutate: async (settings) => {
      await queryClient.cancelQueries({ queryKey });
      const current =
        queryClient.getQueryData<NotificationPreferenceView>(queryKey);
      if (!current) return { previous: {}, optimistic: {} };

      const optimistic = pickChangedChannels(current, settings);
      const previous: ChannelValues = {};
      for (const key of channelKeys(optimistic)) {
        previous[key] = current[key];
      }
      queryClient.setQueryData<NotificationPreferenceView>(queryKey, {
        ...current,
        ...optimistic,
      });
      return { previous, optimistic };
    },
    onError: (_error, _settings, context) => {
      if (!context) return;
      queryClient.setQueryData<NotificationPreferenceView>(
        queryKey,
        (current) => {
          if (!current) return current;
          const restored = { ...current };
          for (const key of channelKeys(context.previous)) {
            if (current[key] === context.optimistic[key]) {
              restored[key] = context.previous[key] as boolean;
            }
          }
          return restored;
        },
      );
    },
    onSuccess: (data, _settings, context) => {
      if (!hasOtherPendingPreferenceUpdates(queryClient)) {
        queryClient.setQueryData(queryKey, data);
        return;
      }
      queryClient.setQueryData<NotificationPreferenceView>(
        queryKey,
        (current) => {
          if (!current) return data;
          const merged = { ...current };
          for (const key of channelKeys(context?.optimistic ?? {})) {
            merged[key] = data[key];
          }
          return merged;
        },
      );
    },
    onSettled: () => {
      if (!hasOtherPendingPreferenceUpdates(queryClient)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
};

export const useUpdateNotificationTypePreferenceMutation = () => {
  const queryClient = useQueryClient();
  const queryKey = notificationPreferencesQueryKey();

  return useMutation<
    NotificationPreferenceView,
    Error,
    { type: string; push: boolean },
    TypeMutationContext
  >({
    mutationKey: [...preferenceMutationKey, "type"],
    mutationFn: ({ type, push }) =>
      notificationPreferenceRepository.updateTypePreference(type, push),
    onMutate: async ({ type, push }) => {
      await queryClient.cancelQueries({ queryKey });
      const current =
        queryClient.getQueryData<NotificationPreferenceView>(queryKey);
      if (!current) return {};

      const previous = current.typePreferences.find(
        (preference) => preference.type === type,
      );
      queryClient.setQueryData(
        queryKey,
        withTypePreference(current, type, { ...previous, type, push }),
      );
      return { previous };
    },
    onError: (_error, { type, push }, context) => {
      queryClient.setQueryData<NotificationPreferenceView>(
        queryKey,
        (current) => {
          if (!current) return current;
          const entry = current.typePreferences.find(
            (preference) => preference.type === type,
          );
          if (entry?.push !== push) return current;
          return withTypePreference(current, type, context?.previous);
        },
      );
    },
    onSuccess: (data, { type }) => {
      if (!hasOtherPendingPreferenceUpdates(queryClient)) {
        queryClient.setQueryData(queryKey, data);
        return;
      }
      queryClient.setQueryData<NotificationPreferenceView>(
        queryKey,
        (current) => {
          if (!current) return data;
          const saved = data.typePreferences.find(
            (preference) => preference.type === type,
          );
          return saved ? withTypePreference(current, type, saved) : current;
        },
      );
    },
    onSettled: () => {
      if (!hasOtherPendingPreferenceUpdates(queryClient)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
};
